import React, { useEffect, useRef, useState } from 'react';

import { ConsentBanner } from './ConsentBanner';
import { ensureGeoResolved, getGeoOutcome } from './consentStore';
import { useConsent } from './useConsent';
import { fireWhenIdle } from '../lib/idleCallback';

/**
 * Decides whether the consent banner is on screen, and keeps it off the
 * critical render path.
 *
 * ## Why the idle gate
 *
 * The landing and occasion pages have had dedicated LCP work
 * (`mgm-occasion-lcp*`), and the banner would otherwise render
 * styled-components during the very first commit of a page view for every
 * visitor it applies to. So the host returns `null` on first render and flips
 * itself on in the browser's idle window.
 * The banner is `position: fixed`, so appearing late costs no layout shift,
 * and it is never the LCP element.
 *
 * This is safe to defer because it is only the UI that waits: the tags are
 * already withheld by the bootstrap in `public/index.html` from the very first
 * byte, whether or not the banner has painted yet.
 *
 * NOT code-split via `React.lazy`. Every first-time visitor needs it, so a
 * separate chunk would trade a few KB of main bundle for an extra round trip
 * on the majority path — and it would put a network fetch between "page
 * loaded" and "user can consent".
 *
 * ## Why it isn't a provider
 *
 * There is no `ConsentProvider`. The store is a plain module
 * (`consentStore.ts`) because the analytics wrappers need a synchronous read
 * with no React in scope; this component just subscribes to it. Mounting it
 * anywhere under `ThemeProvider` is sufficient — it needs the theme and
 * nothing else.
 */
export function ConsentBannerHost(): React.ReactElement | null {
  const { bannerVisible, accept, decline, dismiss } = useConsent();
  const [idleReady, setIdleReady] = useState(false);

  // Kick the geo lookup exactly once. NOT idle-deferred, unlike the banner
  // render below: this is an async network call rather than render work, and
  // every millisecond it waits is a millisecond a non-CA/FL visitor spends
  // with their tags withheld. The store no-ops when a decision is already on
  // file, so a returning visitor makes no call at all.
  useEffect(() => {
    void ensureGeoResolved();
  }, []);

  useEffect(() => {
    // Only pay for the idle callback when a banner is actually pending.
    if (!bannerVisible || idleReady) return;
    fireWhenIdle(() => setIdleReady(true));
  }, [bannerVisible, idleReady]);

  // Report the impression once the banner is genuinely on screen (THE-524).
  //
  // This is the ONLY front-end telemetry this feature has ever had, and the
  // field that justifies it is `geo_resolved`. A banner shown because the
  // server put this visitor in CA/FL and a banner shown because the client
  // never got an answer are the same pixels; until now they were also the same
  // row in analytics, which is how a gate that had been wrong for every slow
  // connection went unnoticed for weeks and surfaced through a user interview.
  // `geo_resolved: false` is the rate to alert on — it should be a rounding
  // error, and when it is not, this is the thing that says so.
  //
  // Fired HERE rather than where `bannerVisible` flips, because that flag is
  // set before the idle gate and a visitor who navigates away in between never
  // actually saw anything. An impression event that counts un-painted banners
  // is worse than none: it would overstate exactly the number being watched.
  //
  // Carries nothing about the visitor. The geo op deliberately returns no
  // location and the outcome holds none, so there is nothing here to leak —
  // and `logEvent` is first-party, not a consent-gated tag.
  // The import is DYNAMIC for the same reason consentGeo.ts defers its own:
  // `eventSink` reaches `firebaseConfig` / `firebase/auth` at module scope, and
  // a static import would put the auth SDK in the module graph of every suite
  // and every chunk that renders a banner. Telemetry is not worth that, and a
  // sink that fails to load must cost an event, never a render — hence the
  // catch.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!bannerVisible || !idleReady || reportedRef.current) return;
    reportedRef.current = true;
    const geo = getGeoOutcome();
    void import('../lib/eventSink')
      .then(({ logEvent }) => {
        logEvent('consent_banner_impression', {
          surface: 'consent_banner',
          // null when geo was never consulted — i.e. the banner was reopened
          // from the privacy page by someone with a decision already on file.
          geo_resolved: geo ? geo.resolved : null,
          geo_source: geo ? geo.source : 'not-consulted',
        });
      })
      .catch(() => undefined);
  }, [bannerVisible, idleReady]);

  if (!bannerVisible || !idleReady) return null;
  return <ConsentBanner onAccept={accept} onDecline={decline} onDismiss={dismiss} />;
}
